import { APP_VERSION } from "./version";
import { getClientMetadata, type ClientMetadata } from "./clientMetadata";

export type DiagnosticEventType =
  | "subscription-management"
  | "subscription-request"
  | "subscription-recovery"
  | "console-error"
  | "network-failure"
  | "unhandled-error"
  | "unhandled-rejection";

export interface DiagnosticEvent {
  timestamp: string;
  type: DiagnosticEventType;
  message: string;
}

export interface FeedbackDiagnostics {
  collectedAt: string;
  app: ClientMetadata;
  screen: string;
  route: string;
  puzzleDate?: string;
  browser: {
    userAgent: string;
    language?: string;
    online?: boolean;
  };
  recentEvents: DiagnosticEvent[];
}

const MAX_EVENTS = 12;
const MAX_EVENT_LENGTH = 800;
const REDACTED = "[REDACTED]";

const recentEvents: DiagnosticEvent[] = [];
let installed = false;

function isSensitiveKey(key: string): boolean {
  return /^(?:authorization|cookie|password|passwd|secret|session|sessiontoken|token|accesstoken|idtoken|refreshtoken|purchasetoken|appaccounttoken|accounttoken|signedapptransaction|credential|apikey)$/i.test(
    key.replace(/[^a-z0-9]/gi, ""),
  );
}

export function sanitizeDiagnosticText(value: string): string {
  return value
    .replace(
      /(["']?(?:authorization|cookie|password|passwd|secret|session|session[_-]?token|access[_-]?token|id[_-]?token|refresh[_-]?token|purchase[_-]?token|app[_-]?account[_-]?token|account[_-]?token|signed[_-]?app[_-]?transaction|token|credential|api[_-]?key)["']?\s*[:=]\s*)(["']?)(?:Bearer\s+)?[^"',;\s}]+/gi,
      `$1$2${REDACTED}`,
    )
    .replace(/\bBearer\s+[^\s,;]+/gi, `Bearer ${REDACTED}`)
    .replace(/\b([A-Za-z0-9_-]{8,})\.([A-Za-z0-9_-]{8,})\.([A-Za-z0-9_-]{8,})\b/g, REDACTED)
    .slice(0, MAX_EVENT_LENGTH);
}

function sanitizeUrl(value: string): string {
  try {
    const url = new URL(value, window.location.href);
    return `${url.origin}${url.pathname}`;
  } catch {
    return sanitizeDiagnosticText(value.split(/[?#]/, 1)[0]);
  }
}

function serializeDiagnosticValue(value: unknown, depth = 0, seen = new WeakSet<object>()): string {
  if (value instanceof Error) {
    return sanitizeDiagnosticText(
      [value.name, value.message, value.stack].filter(Boolean).join(": "),
    );
  }
  if (typeof value === "string") {
    return sanitizeDiagnosticText(value);
  }
  if (
    value === null ||
    typeof value === "number" ||
    typeof value === "boolean" ||
    typeof value === "bigint" ||
    typeof value === "undefined"
  ) {
    return String(value);
  }
  if (typeof value === "function" || typeof value === "symbol") {
    return String(value);
  }
  if (depth >= 3) {
    return "[Truncated]";
  }
  if (seen.has(value)) {
    return "[Circular]";
  }
  seen.add(value);
  if (Array.isArray(value)) {
    return `[${value
      .slice(0, 20)
      .map((item) => serializeDiagnosticValue(item, depth + 1, seen))
      .join(", ")}]`;
  }
  const entries = Object.entries(value)
    .slice(0, 20)
    .map(([key, item]) => {
      const rendered = isSensitiveKey(key)
        ? REDACTED
        : serializeDiagnosticValue(item, depth + 1, seen);
      return `${key}: ${rendered}`;
    });
  return `{ ${entries.join(", ")} }`;
}

export function recordDiagnosticEvent(type: DiagnosticEventType, value: unknown): void {
  const message = sanitizeDiagnosticText(serializeDiagnosticValue(value));
  recentEvents.push({
    timestamp: new Date().toISOString(),
    type,
    message,
  });
  if (recentEvents.length > MAX_EVENTS) {
    recentEvents.splice(0, recentEvents.length - MAX_EVENTS);
  }
}

export function installDiagnosticCapture(): void {
  if (installed || typeof window === "undefined") {
    return;
  }
  installed = true;

  const originalConsoleError = console.error.bind(console);
  console.error = (...args: unknown[]) => {
    recordDiagnosticEvent("console-error", args);
    originalConsoleError(...args);
  };

  window.addEventListener("error", (event) => {
    recordDiagnosticEvent(
      "unhandled-error",
      event.error ??
        `${event.message} at ${sanitizeUrl(event.filename)}:${event.lineno}:${event.colno}`,
    );
  });
  window.addEventListener("unhandledrejection", (event) => {
    recordDiagnosticEvent("unhandled-rejection", event.reason);
  });

  const originalFetch = window.fetch.bind(window);
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const startedAt = Date.now();
    const method =
      init?.method ??
      (typeof Request !== "undefined" && input instanceof Request ? input.method : "GET");
    const url =
      typeof input === "string" || input instanceof URL
        ? String(input)
        : typeof Request !== "undefined" && input instanceof Request
          ? input.url
          : String(input);
    try {
      const response = await originalFetch(input, init);
      if (!response.ok) {
        recordDiagnosticEvent(
          "network-failure",
          `${method.toUpperCase()} ${sanitizeUrl(url)} returned HTTP ${response.status} after ${
            Date.now() - startedAt
          }ms`,
        );
      }
      return response;
    } catch (error) {
      recordDiagnosticEvent(
        "network-failure",
        `${method.toUpperCase()} ${sanitizeUrl(url)} failed after ${
          Date.now() - startedAt
        }ms: ${serializeDiagnosticValue(error)}`,
      );
      throw error;
    }
  };
}

export async function collectFeedbackDiagnostics(screen: string): Promise<FeedbackDiagnostics> {
  let app: ClientMetadata;
  try {
    app = await getClientMetadata();
  } catch {
    app = {
      surface: "web",
      appVersion: APP_VERSION,
      webVersion: APP_VERSION,
    };
  }

  return {
    collectedAt: new Date().toISOString(),
    app,
    screen,
    route: window.location.pathname,
    browser: {
      userAgent: navigator.userAgent,
      ...(navigator.language ? { language: navigator.language } : {}),
      ...(typeof navigator.onLine === "boolean" ? { online: navigator.onLine } : {}),
    },
    recentEvents: recentEvents.map((event) => ({ ...event })),
  };
}

export function resetDiagnosticsForTests(): void {
  recentEvents.length = 0;
}
